import React, { useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import { ArrayLike } from 'd3';
import { linkHorizontal, linkVertical } from './layout/layout';
import { HierarchyPointNode } from 'd3-hierarchy';
import {
  BusinessJourneyLayout,
  defaultNodeHeight,
  defaultNodeWidth,
  HierarchyData,
  stroke,
  strokeLinecap,
  strokeLinejoin,
  strokeWidth,
} from './model';
import { useComputeLayout } from './layout/useComputeLayout';

type BusinessJourneyGraphProps<DT extends HierarchyData> = {
  nodesData: DT[];
  nodeRenderer: (node: HierarchyPointNode<DT>) => JSX.Element;
  nodeWidth?: number;
  nodeHeight?: number;
  layout?: BusinessJourneyLayout;
};

export function BusinessJourneyGraph<DT extends HierarchyData>(props: BusinessJourneyGraphProps<DT>) {
  const {
    layout = 'horizontal',
    nodesData = [],
    nodeRenderer,
    nodeWidth = defaultNodeWidth,
    nodeHeight = defaultNodeHeight,
  } = props;

  const svgDivRef = useRef(null);

  const { root, canvasHeight, canvasWidth } = useComputeLayout<DT>({ nodesData, layout, nodeWidth, nodeHeight });

  useEffect(() => {
    if (!svgDivRef.current) return;

    const linkPathGenerator = layout === 'horizontal' ? linkHorizontal : linkVertical;

    const svg = d3
      .select(svgDivRef.current)
      .select('svg')
      .attr('width', canvasWidth)
      .attr('height', canvasHeight)
      .attr('viewBox', [0, 0, canvasWidth, canvasHeight]);

    svg
      .select('#linksGroup')
      .attr('stroke', stroke)
      .attr('stroke-opacity', 1)
      .attr('stroke-linecap', strokeLinecap)
      .attr('stroke-linejoin', strokeLinejoin)
      .attr('stroke-width', strokeWidth)
      .selectAll('path')
      .data(root.links())
      .join('path')
      .attr('d', d => linkPathGenerator(d, nodeWidth, nodeHeight))
      .attr('marker-end', `url(#arrow-${layout})`);

    // transform the nodes to their new positions

    d3.selectAll<HTMLDivElement, DT>('.businessJourneyNode')
      .data<HierarchyPointNode<DT>>(
        root.descendants(),
        // @ts-ignore
        (d: HierarchyPointNode<DT>, index: number, elements: ArrayLike<HTMLDivElement> | HTMLDivElement[]): string =>
          d ? d.data.id.toString() : elements[index]?.id,
      )
      .style('position', 'absolute')
      .style('width', `${nodeWidth}px`)
      .style('height', `${nodeHeight}px`)
      .style('transform', d => `translate(${d.x}px, ${d.y}px) scale(1)`);
  }, [svgDivRef.current, nodesData, layout, root, canvasWidth, canvasHeight, nodeHeight, nodeWidth, nodeRenderer]);

  const nodesElement = useMemo(() => {
    return root.descendants().map((d: HierarchyPointNode<DT>) => (
      <div key={d.data.id} id={d.data.id.toString()} className="businessJourneyNode">
        {nodeRenderer(d)}
      </div>
    ));
  }, [root, nodeRenderer]);

  return (
    <div ref={svgDivRef} style={{ position: 'relative', width: `${canvasWidth}px`, height: `${canvasHeight}px` }}>
      <svg>
        <g id="linksGroup"></g>
        <defs>
          <marker
            id="arrow-horizontal"
            markerWidth="10"
            markerHeight="9"
            refX="8"
            refY="9"
            viewBox="0 0 16 9"
            orient="-90deg"
          >
            <path
              d="M7.29289 8.70711C7.68341 9.09763 8.31658 9.09763 8.7071 8.70711L15.0711 2.34316C15.4616 1.95264 15.4616 1.31947 15.0711 0.928946C14.6806 0.538421 14.0474 0.53842 13.6569 0.928943L8 6.58579L2.34316 0.928921C1.95263 0.538396 1.31947 0.538395 0.928944 0.928919C0.538419 1.31944 0.538418 1.95261 0.928941 2.34313L7.29289 8.70711ZM7 7L7 8L9 8L9 7L7 7Z"
              fill={stroke}
            />
          </marker>
          <marker id="arrow-vertical" markerWidth="10" markerHeight="9" refX="8" refY="9" viewBox="0 0 16 9">
            <path
              d="M7.29289 8.70711C7.68341 9.09763 8.31658 9.09763 8.7071 8.70711L15.0711 2.34316C15.4616 1.95264 15.4616 1.31947 15.0711 0.928946C14.6806 0.538421 14.0474 0.53842 13.6569 0.928943L8 6.58579L2.34316 0.928921C1.95263 0.538396 1.31947 0.538395 0.928944 0.928919C0.538419 1.31944 0.538418 1.95261 0.928941 2.34313L7.29289 8.70711ZM7 7L7 8L9 8L9 7L7 7Z"
              fill={stroke}
            />
          </marker>
        </defs>
      </svg>
      <div className="nodesGroup" style={{ position: 'absolute', left: 0, top: 0 }}>
        {nodesElement}
      </div>
    </div>
  );
}
